import { v4 as uuidv4 } from 'uuid';
import {
  CurrencyType,
  FulfillmentOrdersResponseDto,
  FulfillmentResponseDto,
  LogEntryResponseDto,
} from '@tiendanube/common';
import { LogType, Status } from '@tiendanube/common/src/enums';
import { Timeline, Card } from '@tiendanube/components';
import { PAYMENT_STATUS_EXPIRED_ENABLED } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import useTranslationOrders from 'domains/Orders/useTranslationOrders';
import CardTimelineErrorState from './CardTimelineErrorState';
import CardTimelineSkeleton from './CardTimelineSkeleton';
import useCardTimeline from './useCardTimeline';

interface CardTimelineProps {
  fulfillment: FulfillmentResponseDto;
  fulfillmentOrders?: FulfillmentOrdersResponseDto[];
  logEntries: LogEntryResponseDto[];
  isShippablePickup: boolean;
  isManualPayment: boolean;
  orderCurrency: CurrencyType;
}

function CardTimeline({
  logEntries,
  fulfillment,
  fulfillmentOrders,
  isShippablePickup,
  orderCurrency,
  isManualPayment,
}: CardTimelineProps) {
  const t = useTranslationOrders();
  const [hasExpiredTag] = useHasTags([PAYMENT_STATUS_EXPIRED_ENABLED]);
  const timelineProps = useCardTimeline({
    fulfillment,
    fulfillmentOrders,
    isShippablePickup,
    orderCurrency,
  });

  const logClone = [...logEntries];

  const timeLogs = logClone
    .reverse()
    .filter(({ type, data2: action }) => {
      const pendingManualPayments =
        action === Status.PENDING && isManualPayment;
      const refundOrderEvents =
        type === 'order-payment' &&
        [Status.REFUNDED, Status.PARTIALLY_REFUNDED].includes(action as Status);
      const incomePayment = type === LogType.TRANSACTION && action === 'paid';

      return !pendingManualPayments && !refundOrderEvents && !incomePayment;
    })
    .map((log) => {
      // If user doesn't have the expired tag, show EXPIRED as VOIDED
      const transformedLog =
        log.type === 'order-payment' &&
        log.data2 === Status.EXPIRED &&
        !hasExpiredTag
          ? { ...log, data2: Status.VOIDED }
          : log;

      return (
        <Timeline.Item key={uuidv4()} {...timelineProps(transformedLog)} />
      );
    });

  if (timeLogs.length === 0) return null;

  return (
    <Card title={t('cardTimeline.title')}>
      <Timeline>{timeLogs}</Timeline>
    </Card>
  );
}

CardTimeline.Skeleton = CardTimelineSkeleton;
CardTimeline.ErrorState = CardTimelineErrorState;

export default CardTimeline;
